<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getRecord, recordAction } from '@/lib/records.js'
import { saveFilling } from '@/lib/saveFilling.js'
import { useSave, reportError } from '@/composables/useSave.js'
import UiButton from '@/components/ui/UiButton.vue'
import UiMenu from '@/components/ui/UiMenu.vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiSpinner from '@/components/ui/UiSpinner.vue'
import RecordForm from '@/components/form/RecordForm.vue'
import ActivityCloseDialog from '@/components/activity/ActivityCloseDialog.vue'
import { addPhone, callNoteTarget, phoneTarget, textDoc } from '@/lib/callNotes.js'
import { closedText } from '@/lib/followUp.js'
import { telHref } from '@/lib/phone.js'
import { api } from '@/lib/http.js'

/**
 * What can be done with an activity, the same wherever it is shown (work list, timeline,
 * record card), as in Dynamics: an open one is completed, edited or cancelled; a completed or
 * cancelled one is read-only and can only be reopened. The buttons follow the abilities the
 * server (or the replica, offline) gives the activity.
 *
 *   compact   an icon for "complete" and the rest in a menu (rows of a list)
 *   noEdit    the caller shows the form itself (the record card)
 *   phone     a number to call (an open call): "Call" dials it and opens the call's summary at
 *             once, waiting for the person when they come back from the phone app; its summary
 *             becomes a note on what the call is about (or on whom, or on the call itself).
 *             An open call with no number shows a crossed-out phone instead: the same screen, with
 *             the number to add above it; the number and/or what was said on a call made another
 *             way can be saved leaving the task open, or the task completed as usual.
 */
const props = defineProps({
  activity: { type: Object, required: true },
  compact: Boolean,
  noEdit: Boolean,
  size: { type: String, default: 'sm' },
  phone: { type: String, default: null },
  person: { type: Object, default: null }, // { entity, uuid, label } — whom the call is with
})
const emit = defineEmits(['changed'])
const { t } = useI18n()
const { report } = useSave()

const a = computed(() => props.activity)
const can = (ability) => a.value.abilities?.[ability] !== false
const isOpen = computed(() => a.value.status === 'open')
const canComplete = computed(() => isOpen.value && can('complete'))
const canCancel = computed(() => isOpen.value && can('cancel'))
const canEdit = computed(() => !props.noEdit && isOpen.value && can('update'))
const canReopen = computed(() => !isOpen.value && can('reopen'))

const closing = ref(null) // 'complete' | 'cancel'
const calling = ref(null) // { phone, person } while the call's summary is open
const canCall = computed(() => !!props.phone && a.value.type === 'call' && canComplete.value)
const noNumber = computed(() => !props.phone && a.value.type === 'call' && isOpen.value && canComplete.value)
/** The call's screen with a place for its number above. */
function withoutNumber() {
  calling.value = { phone: null, person: props.person, numberFor: phoneTarget(a.value, props.person) }
  closing.value = 'complete'
}
/** The number where it belongs, and what was said as a note; false when one could not be saved. */
async function saveAround({ phone, summary }) {
  if (phone && calling.value?.numberFor) {
    const result = await addPhone(calling.value.numberFor, phone.value, phone.kind)
    report(result, { success: t('activity.callDetails.phoneSaved') })
    if (!['saved', 'queued'].includes(result.status)) return false
  }
  if (summary) {
    const result = await saveFilling('note', null, { noteable: callNoteTarget(a.value, props.person), body: textDoc(summary) }, { label: t('activity.call.summary') })
    report(result, { success: t('activity.callDetails.noteSaved') })
    if (!['saved', 'queued'].includes(result.status)) return false
  }
  return true
}
/** Saved, the task left open. */
async function keep(payload) {
  try {
    if (!(await saveAround(payload))) return
  } catch (e) {
    return reportError(e)
  }
  closing.value = null
  calling.value = null
  emit('changed', { action: 'edit' })
}

/** Dial, and have the summary waiting on return from the phone app. */
function call() {
  if (!canCall.value) return
  calling.value = { phone: props.phone, person: props.person }
  closing.value = 'complete'
  // Dialed from here: a quarter of an hour later its summary is asked for, if none was written (D-257).
  if (props.activity?.uuid && props.activity.type === 'call') api(`/activities/${props.activity.uuid}/dialed`, { method: 'POST' }).catch(() => null)
  window.location.href = telHref(props.phone)
}
// The work list's keys and swipes (D-297): close it from outside, as its own buttons do.
const complete = () => canComplete.value && (closing.value = 'complete')
const cancel = () => canCancel.value && (closing.value = 'cancel')
defineExpose({ call, canCall, complete, cancel, withoutNumber })

const editing = ref(null) // the full record, once read
const loadingEdit = ref(false)

async function close({ action, body, phone }) {
  const { summary, ...rest } = body
  if (phone) {
    try {
      if (!(await saveAround({ phone, summary: null }))) return
    } catch (e) {
      return reportError(e)
    }
  }
  const result = await recordAction('activity', a.value.uuid, action, rest, { label: a.value.label || a.value.subject })
  report(result, { success: closedText(t, action, rest) })
  if (['saved', 'queued'].includes(result.status)) {
    // The conversation's summary: a note where the call's story is read (saved, or queued offline).
    if (summary) await saveAround({ phone: null, summary }).catch(reportError)
    closing.value = null
    calling.value = null
    emit('changed', { action, record: result.record })
  }
}
async function reopen() {
  const result = await recordAction('activity', a.value.uuid, 'reopen', {}, { label: a.value.label || a.value.subject })
  report(result, { success: t('activity.reopened') })
  if (['saved', 'queued'].includes(result.status)) emit('changed', { action: 'reopen', record: result.record })
}
async function edit() {
  loadingEdit.value = true
  editing.value = {}
  try {
    editing.value = (await getRecord('activity', a.value.uuid)).data
  } catch (e) {
    editing.value = null
    reportError(e)
  } finally {
    loadingEdit.value = false
  }
}
function saved(record) {
  editing.value = null
  emit('changed', { action: 'edit', record })
}

const menu = computed(() => [
  canEdit.value && { label: t('common.edit'), icon: 'fa-pen', action: edit },
  canCancel.value && { label: t('activity.cancelActivity'), icon: 'fa-ban', danger: true, action: () => (closing.value = 'cancel') },
  canReopen.value && { label: t('actions.reopen'), icon: 'fa-rotate-left', action: reopen },
].filter(Boolean))
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5" data-testid="activity-actions" @click.stop>
    <template v-if="compact">
      <button v-if="canCall" type="button" class="rounded-full p-1.5 text-faint hover:bg-brand-100 hover:text-brand-700" :title="$t('activity.call.dial', { phone })" :data-testid="`call-${a.uuid}`" @click="call">
        <i class="fa-solid fa-phone text-base" aria-hidden="true"></i>
      </button>
      <button v-if="noNumber" type="button" class="rounded-full p-1.5 text-faint hover:bg-amber-100 hover:text-amber-700" :title="$t('activity.callDetails.noNumber')" :data-testid="`no-number-${a.uuid}`" @click="withoutNumber">
        <i class="fa-solid fa-phone-slash text-base" aria-hidden="true"></i>
      </button>
      <button v-if="canComplete" type="button" class="rounded-full p-1.5 text-faint hover:bg-emerald-100 hover:text-emerald-700" :title="$t('actions.complete')" :data-testid="`complete-${a.uuid}`" @click="closing = 'complete'">
        <i class="fa-regular fa-circle-check text-lg" aria-hidden="true"></i>
      </button>
      <UiMenu v-if="menu.length" :items="menu" :data-testid="`activity-menu-${a.uuid}`" />
    </template>
    <template v-else>
      <UiButton v-if="canCall" :size="size" variant="primary" icon="fa-phone" data-testid="action-call" @click="call">{{ $t('activity.call.button') }}</UiButton>
      <UiButton v-if="noNumber" :size="size" icon="fa-phone-slash" :title="$t('activity.callDetails.noNumber')" data-testid="action-no-number" @click="withoutNumber">{{ $t('activity.callDetails.button') }}</UiButton>
      <UiButton v-if="canComplete" :size="size" variant="success" icon="fa-check" data-testid="action-complete" @click="closing = 'complete'">{{ $t('actions.complete') }}</UiButton>
      <UiButton v-if="canEdit" :size="size" icon="fa-pen" data-testid="action-edit" @click="edit">{{ $t('common.edit') }}</UiButton>
      <UiButton v-if="canCancel" :size="size" icon="fa-ban" data-testid="action-cancel-activity" @click="closing = 'cancel'">{{ $t('activity.cancelActivity') }}</UiButton>
      <UiButton v-if="canReopen" :size="size" icon="fa-rotate-left" data-testid="action-reopen" @click="reopen">{{ $t('actions.reopen') }}</UiButton>
    </template>

    <ActivityCloseDialog v-if="closing" :activity="a" :mode="closing" :call="calling" @close="closing = null; calling = null" @confirm="close" @keep="keep" />
    <UiModal :open="!!editing" :title="$t('activity.editTitle')" size="lg" @close="editing = null">
      <div v-if="loadingEdit" class="py-10 text-center"><UiSpinner /></div>
      <RecordForm v-else-if="editing?.uuid" entity="activity" :record="editing" @saved="saved" @cancel="editing = null" />
    </UiModal>
  </div>
</template>
